export default class HtmlUtils {

    /**
     * Verifica se o hostname da janela atual é "localhost" ou se contém os prefixos "10.", "127.", "192." ou "172.16.", indicando que a aplicação está sendo executada em um ambiente local. Retorna true se o hostname corresponder a qualquer um desses critérios, ou false caso contrário.
     * @returns
     */
    static isLocalhost(): boolean {
        return window.location.hostname == "localhost"
            || window.location.hostname.indexOf("10.") > -1
            || window.location.hostname.indexOf("127.") > -1
            || window.location.hostname.indexOf("192.") > -1
            || window.location.hostname.indexOf("172.16.") > -1
            ;
    }

    /**
     * Extrai o domínio de uma URL, removendo o protocolo (http:// ou https://) e quaisquer caminhos ou parâmetros adicionais. A função divide a URL usando o caractere '/' e retorna a parte correspondente ao domínio, que geralmente é a terceira parte da URL (índice 2), mas pode ser a primeira parte (índice 0) se a URL não contiver um protocolo. Além disso, a função remove qualquer porta especificada no domínio, dividindo novamente a parte do domínio usando o caractere ':' e retornando apenas a parte antes dos dois pontos.
     * @param url A URL da qual o domínio deve ser extraído, que pode ser um endereço completo (começando com http:// ou https://) ou um caminho relativo (como "/pagina"). Se a URL for relativa, a função tentará extrair o domínio da parte correspondente, mas isso pode não ser confiável se a URL não contiver um protocolo ou se tiver um formato incomum.
     * @returns
     */
    static getDomain(url: string): string {
        return url ? (url.split('/')[2] || url.split('/')[0]).split(':')[0] : url;
    }

    /**
     * Retorna a localização completa do site atual, incluindo o protocolo (http:// ou https://), o hostname e a porta (se houver). A localização é construída usando as propriedades window.location.protocol, window.location.hostname e window.location.port, garantindo que o formato da URL seja correto. Se a porta estiver presente, ela é adicionada à URL com um prefixo de dois pontos (:), caso contrário, a URL é construída apenas com o protocolo e o hostname.
     * @returns
     */
    static getFullLocation(): string {
        return window.location.protocol
            + "//"
            + window.location.hostname
            + (window.location.port ? ":" + window.location.port : "");
    }

    /**
     * Converte uma URL relativa em uma URL completa, usando a localização atual do site como base. Se a URL já for completa (começando com http:// ou https://), ela é retornada sem alterações. Caso contrário, a URL é concatenada com a localização atual do site, garantindo que o caminho seja correto, adicionando uma barra (/) entre a localização e a URL se necessário.
     * @param url A URL a ser convertida, que pode ser um endereço completo (começando com http:// ou https://) ou um caminho relativo (como "/pagina"). Se a URL for relativa, ela será convertida para uma URL completa usando a localização atual do site como base.
     * @returns
     */
    static getFullUrl(url: string): string {
        return url.startsWith("http") ? url : this.getFullLocation() + (url.startsWith("/") ? "" : "/") + url.trimStart();
    }

    /**
     * Baixa um arquivo a partir de uma URL, criando um elemento <a> temporário e simulando um clique para iniciar o download. O parâmetro filename é opcional e pode ser usado para especificar o nome do arquivo a ser baixado. Se filename não for fornecido, o nome do arquivo será gerado com base no timestamp atual. Após o clique simulado, o elemento <a> é removido do DOM.
     * @param url A URL do arquivo a ser baixado, que pode ser um endereço completo (começando com http:// ou https://) ou um caminho relativo (como "/arquivo"). Se a URL for relativa, ela será convertida para uma URL completa usando a localização atual do site como base.
     * @param filename O nome do arquivo a ser baixado, que pode incluir a extensão do arquivo (como "documento.pdf"). Se não for fornecido, o nome do arquivo será gerado com base no timestamp atual, usando new Date().getTime().toString() para criar um nome único.
     */
    static async downloadUrl(url: string, filename?: string | null): Promise<Promise<void>> {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename ?? new Date().getTime().toString();

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(blobUrl);
    }

    /**
     * Abre uma URL em uma nova janela ou guia do navegador, usando o método window.open. Se a janela atual tiver um opener (ou seja, se for uma janela secundária), a URL será aberta na mesma janela, a menos que a opção forceNewWindow seja definida como true. As opções noopener e noreferrer podem ser usadas para melhorar a segurança da janela aberta, evitando que ela tenha acesso ao objeto window.opener ou que envie informações de referência para a URL aberta. Retorna a referência para a janela aberta, ou null se a abertura falhar.
     * @param url A URL a ser aberta, que pode ser um endereço completo (começando com http:// ou https://) ou um caminho relativo (como "/pagina"). Se a URL for relativa, ela será convertida para uma URL completa usando a localização atual do site como base.
     * @param windowName O nome da janela a ser aberta, que pode ser usado para referenciar a janela posteriormente. Se não for fornecido, a janela será aberta com um nome gerado automaticamente pelo navegador.
     * @param features As opções de janela a serem usadas ao abrir a URL, que podem incluir:
     * @returns
     */
    static openUrlInNewWindow(
        url: string,
        windowName?: string,
        features?: {
            noopener?: boolean;
            noreferrer?: boolean;
            forceNewWindow?: boolean;
        }
    ): Window | null {

        if (window.opener && features?.forceNewWindow !== true) {

            // Se já está em uma página secundária,
            // mantém a URL na mesma página se for algum 'viewer'.
            var aux = document.location.pathname.toLowerCase().split('/');
            if (aux.length > 3 && aux[2].endsWith('viewer')) {
                document.location.href = url;
                return window;
            }
        }

        var f: Array<string> = [];
        if (features?.forceNewWindow) f = ["location=0", "menubar=true", "resizable=true", "scrollbars=true", "status=true", "toolbar=false", "height=600", "width=800"];
        if (features?.noopener == true) f.push("noopener=true");
        if (features?.noreferrer == true) f.push("noreferrer=true");

        return window.open(this.getFullUrl(url), windowName, f.join(","));
    }

    /**
     * Abre o player de CDS em uma nova janela, usando os parâmetros fornecidos para construir a URL e as opções de janela. A URL é construída a partir da localização atual do site, adicionando o caminho "/cds" e os parâmetros fornecidos como query string. As opções de janela são configuradas com base nos parâmetros maximize, width e height, permitindo que a janela seja maximizada ou tenha dimensões específicas. Retorna a referência para a janela aberta, ou null se a abertura falhar.
     * @param parameters Os parâmetros a serem adicionados à URL do player de CDS, que devem ser fornecidos como uma string de query string, como "param1=value1&param2=value2". Esses parâmetros serão adicionados à URL do player de CDS para configurar o comportamento do player, como o conteúdo a ser exibido ou as opções de reprodução.
     * @param windowName O nome da janela a ser aberta, que pode ser usado para referenciar a janela posteriormente. Se não for fornecido, a janela será aberta com um nome gerado automaticamente pelo navegador.
     * @param maximize Um booleano que indica se a janela deve ser maximizada ao ser aberta. Se true, a janela será aberta com as dimensões máximas disponíveis na tela do usuário. Se false ou não fornecido, a janela será aberta com as dimensões especificadas pelos parâmetros width e height, ou com as dimensões padrão do navegador se width e height não forem fornecidos.
     * @param width A largura da janela a ser aberta, em pixels. Se maximize for true, esse parâmetro será ignorado e a janela será aberta com a largura máxima disponível na tela do usuário. Se maximize for false ou não fornecido, a janela será aberta com a largura especificada por esse parâmetro, ou com a largura padrão do navegador se esse parâmetro não for fornecido.
     * @param height A altura da janela a ser aberta, em pixels. Se maximize for true, esse parâmetro será ignorado e a janela será aberta com a altura máxima disponível na tela do usuário. Se maximize for false ou não fornecido, a janela será aberta com a altura especificada por esse parâmetro, ou com a altura padrão do navegador se esse parâmetro não for fornecido.
     * @returns
     */
    static openCdsPlayer(
        parameters: string,
        windowName?: string,
        maximize?: boolean,
        width?: number | null,
        height?: number | null
    ): Window | null {

        var f: Array<string> = [];
        f.push("toolbar=no");
        f.push("menubar=no");

        if (width || height || maximize)
            f.push("resizable=no");

        if (maximize === true) {
            f.push(`width=${window.screen.availWidth}px`);
            f.push(`height=${window.screen.availHeight}px`);
            f.push("top=0");
        }
        else {
            if (width) f.push(`width=${width}`);
            if (height) f.push(`height=${height}`);
            if (width && height) {
                // Centraliza
                f.push(`left=${(window.screen.availWidth - width) / 2}`);
                f.push(`top=${(window.screen.availHeight - height) / 2}`);
            }
        }

        return window.open(this.getFullLocation() + "/cds?" + parameters, windowName, f.join(","));
    }

    /**
     * Fecha a janela atual, usando o método window.close do navegador. Se a janela não puder ser fechada (por exemplo, se for a janela principal), o método não fará nada.
     * @param window A janela a ser fechada, que pode ser a janela atual ou uma janela secundária aberta pelo método openUrlInNewWindow ou openCdsPlayer. O método window.close do navegador é chamado para fechar a janela especificada.
     * @returns
     */
    static closeWindow(window: Window): void {
        return window.close();
    }

    /**
     * Verifica se uma string contém tags HTML, usando uma expressão regular que procura por tags de abertura e fechamento, ou por tags auto-fecháveis. Retorna true se a string contiver tags HTML, ou false caso contrário.
     * @param str A string a ser verificada, que pode conter tags HTML. A expressão regular procura por tags de abertura e fechamento, como <tag>...</tag>, ou por tags auto-fecháveis, como <tag/> ou <tag>.
     * @returns
     */
    static isHtml(str: string): boolean {
        return /<(br|basefont|hr|input|source|frame|param|area|meta|!--|col|link|option|base|img|wbr|!DOCTYPE).*?>|<(a|abbr|acronym|address|applet|article|aside|audio|b|bdi|bdo|big|blockquote|body|button|canvas|caption|center|cite|code|colgroup|command|datalist|dd|del|details|dfn|dialog|dir|div|dl|dt|em|embed|fieldset|figcaption|figure|font|footer|form|frameset|head|header|hgroup|h1|h2|h3|h4|h5|h6|html|i|iframe|ins|kbd|keygen|label|legend|li|map|mark|menu|meter|nav|noframes|noscript|object|ol|optgroup|output|p|pre|progress|q|rp|rt|ruby|s|samp|script|section|select|small|span|strike|strong|style|sub|summary|sup|table|tbody|td|textarea|tfoot|th|thead|time|title|tr|track|tt|u|ul|var|video).*?<\/\2>/i.test(str);
    }

    /**
     * Escapa os caracteres especiais &, < e > em uma string, substituindo-os por suas entidades HTML correspondentes (&amp;, &lt; e &gt; respectivamente). Isso é útil para evitar que esses caracteres sejam interpretados como tags HTML ou entidades, garantindo que a string seja exibida corretamente em um contexto HTML.
     * @param str A string a ser escapada, onde os caracteres especiais &, < e > serão substituídos por suas entidades HTML correspondentes (&amp;, &lt; e &gt; respectivamente).
     * @returns
     */
    static escape(str: string): string {
        return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    /**
     * Codifica uma string usando encodeURIComponent, mas também codifica caracteres adicionais que não são codificados por encodeURIComponent, como ! ' " % ( ) * < >
     * @param value A string a ser codificada
     * @returns
     */
    static encodeUri(value: string) {
        return encodeURIComponent(value).replace(/[!'"%()*<>]/g, function (c) {
            return '%' + c.charCodeAt(0).toString(16).toUpperCase();
        });
    }

    /**
     * Verifica se as coordenadas do mouse estão dentro dos limites de um elemento HTML, usando as propriedades getBoundingClientRect do elemento e as propriedades clientX e clientY do evento de mouse. Retorna true se o mouse estiver dentro do elemento, ou false caso contrário.
     * @param element O elemento HTML a ser verificado
     * @param m O evento de mouse contendo as coordenadas do mouse a serem verificadas
     * @returns
     */
    static checkMouseInsideHtmlElement(element: HTMLElement, m: MouseEvent): boolean {
        const dropdown = element.getBoundingClientRect();
        const target = { y: m.clientY, x: m.clientX };
        return target.y >= dropdown.top && target.y <= dropdown.bottom && target.x >= dropdown.left && target.x <= dropdown.right;
    }

    /**
     * Converte quebras de linha em uma string para tags <br/>, permitindo que o texto seja exibido corretamente em HTML. Se a string for nula ou vazia, retorna a string original.
     * @param text
     * @returns
     */
    static textToHtml(text: string): string {
        if (text) {
            return text.replace(/\n/g, "<br/>");
        }
        return text;
    }

    /**
     * Detecta as dimensões da tela do usuário, usando as propriedades window.innerWidth e window.innerHeight, ou document.body.clientWidth e document.body.clientHeight como fallback. Retorna um objeto com as propriedades width e height, representando a largura e altura da tela do usuário em pixels.
     * @returns
     */
    static screenDimension() {
        var x, y;
        if (window.innerWidth) {
            x = window.innerWidth;
            y = window.innerHeight;
        } else {
            x = document.body.clientWidth;
            y = document.body.clientHeight;
        }

        return { width: x, height: y };
    }

    /**
     * Detecta se o dispositivo do usuário é um iPad, verificando a plataforma do navegador. Retorna true se for um iPad, ou false caso contrário.
     * @returns
     */
    static isIPad(): boolean {
        const m = navigator.platform.match(/iPad/i);
        return m != null && m.length > 0;
    }

    /**
     * Copia um texto para a área de transferência do usuário, usando a API Clipboard do navegador. Retorna uma promise que é resolvida quando o texto é copiado com sucesso, ou rejeitada se ocorrer um erro.
     * @param text O texto a ser copiado para a área de transferência do usuário
     * @returns
     */
    static copyToClipboard(text: string) {
        return navigator.clipboard.writeText(text);
    }

    /**
     * Abre o Google Maps para um local específico, usando o endereço, cidade e estado. O endereço deve ser separado por vírgulas, como "Rua ABC, 123".
     * @param address O endereço do local a ser aberto no Google Maps, separado por vírgulas, como "Rua ABC, 123".
     * @param city A cidade do local a ser aberto no Google Maps
     * @param state O estado do local a ser aberto no Google Maps
     */
    static openGoogleMaps(address: string, city: string, state: string) {
        const url = "https://www.google.com/maps/place/" + address + "," + city + "," + state;
        window.open(url, "");
    }

    /**
     * Abre o Google Maps para um local específico, usando uma string de consulta. A string pode ser um endereço completo, ou apenas um nome de local, como "Eiffel Tower".
     * @param place A string de consulta para o local a ser aberto no Google Maps
     */
    static openGoogleMapsFromPlace(place: string) {
        const url = "https://www.google.com/maps/place/" + place;
        window.open(url, "");
    }

    /**
     * Constrói uma query string a partir de um objeto, ignorando os campos com valor nulo ou undefined.
     * @param obj O objeto a ser convertido em query string
     * @returns
     */
    static buildQueryString(obj: object) {
        return Object.entries(obj)
            .filter(([k, v]) => v != null)
            .map(([k, v]) => `${k}=${v}`)
            .join('&');
    }

    /**
     * Recebe uma promise de download de arquivo (que deve retornar um blob) e baixa o arquivo, usando o nome do arquivo retornado no header "content-disposition".
     * @param promise A promise de download de arquivo, que deve retornar um blob e incluir o nome do arquivo no header "content-disposition". O método processa a resposta da promise para extrair o nome do arquivo e o blob, e em seguida cria um elemento <a> temporário para iniciar o download do arquivo. O nome do arquivo é extraído do header "content-disposition", decodificado se necessário, e usado como o nome do arquivo a ser baixado. Se a resposta da promise indicar que nenhum arquivo foi recebido (status 204), uma mensagem de erro é lançada.
     * @returns
     */
    static downloadBlobFromPromise(promise: Promise<any>) {
        var filename = "";

        promise
            .then(response => {
                if (response) {
                    if (response.status == 204)
                        throw "Nenhum arquivo foi recebido.";

                    const disposition = response.headers['content-disposition'];
                    filename = disposition.split(/;(.+)/)[1].split(/=(.+)/)[1];
                    if (filename.toLowerCase().startsWith("utf-8''"))
                        filename = decodeURIComponent(filename.replace("utf-8''", ''));
                    else
                        filename = filename.replace(/['"]/g, '');
                    return response.data;
                }
                return null;
            })
            .then(blob => {
                if (blob != null) {
                    var url = window.URL.createObjectURL(blob);
                    var a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a); // append the element to the dom
                    a.click();
                    a.remove(); // afterwards, remove the element
                }
            });

        return promise;
    }

    // static downloadBlob(blob: Blob, filename?: string): void {
    //     const objectUrl = window.URL.createObjectURL(blob);
    //     const anchor = document.createElement("a");
    //     document.body.appendChild(anchor);
    //     anchor.href = objectUrl;
    //     anchor.download = filename || (new Date().getTime().toString());
    //     anchor.click();
    //     window.URL.revokeObjectURL(objectUrl);
    //     document.body.removeChild(anchor);
    // }

}

export type ScreenPoint = { x: number; y: number; };
